/**
 * Agent Skills export — the "Export" leg of MCP / A2A / Export.
 *
 * Projects the workspace's Standards (checks, project rules, tech stack, prompt
 * templates) into portable `SKILL.md` files (the swe-skills / "skills standard"
 * format) the user commits to their repo, so any agent harness follows them with
 * no MCP server or API key. The bundle is generated server-side (space-scoped)
 * and zipped in the browser — see lib/zip.ts.
 */

import { useEffect, useState } from 'react';
import {
  FolderDown,
  Download,
  FileCode,
  AlertTriangle,
  Copy,
  Check,
} from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import { skillsExportApi, type SkillFile } from '@/services/skillsExportApi';
import { createZip } from '@/lib/zip';
import { cn } from '@/lib/utils';

type State =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; files: SkillFile[]; generatedAt: string };

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function SkillsExportSection() {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [selected, setSelected] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    skillsExportApi.export().then(
      (result) => {
        if (cancelled) return;
        setState({ status: 'loaded', files: result.files, generatedAt: result.generatedAt });
        setSelected(result.files[0]?.path ?? null);
      },
      () => {
        if (!cancelled) setState({ status: 'error' });
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  function handleDownload() {
    if (state.status !== 'loaded') return;
    download(createZip(state.files), 'systemdox-skills.zip');
  }

  const active =
    state.status === 'loaded' ? state.files.find((f) => f.path === selected) : undefined;

  function handleCopy() {
    if (!active) return;
    navigator.clipboard.writeText(active.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <section>
      <div className="flex items-center gap-2 mb-1">
        <FolderDown className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          Download as Agent Skills
        </h2>
      </div>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
        Export your checks, rules and tech stack as portable{' '}
        <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">SKILL.md</code>{' '}
        files. Commit the <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">skills/</code>{' '}
        folder to your repo and any agent harness (Claude Code, Codex, Cursor, Gemini CLI,
        Copilot) follows them — no MCP server or API key required.
      </p>

      {state.status === 'loading' ? (
        <div className="flex items-center justify-center h-24 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <Spinner size="md" />
        </div>
      ) : state.status === 'error' ? (
        <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Couldn't generate the bundle — the standards API may be unavailable. Try again shortly.
        </div>
      ) : state.files.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-6 text-center text-sm text-slate-500 dark:text-slate-400">
          Nothing to export yet. Add checks, project rules, a tech stack, or prompt
          templates and they'll appear here as installable skills.
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {state.files.length} file{state.files.length === 1 ? '' : 's'} generated
            </p>
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Download systemdox-skills.zip
            </button>
          </div>

          {/* File browser */}
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,14rem)_1fr]">
            <ul className="border-b sm:border-b-0 sm:border-r border-slate-200 dark:border-slate-800 max-h-72 overflow-y-auto py-1">
              {state.files.map((f) => (
                <li key={f.path}>
                  <button
                    onClick={() => setSelected(f.path)}
                    className={cn(
                      'flex w-full items-center gap-1.5 px-3 py-1.5 text-left text-xs font-mono truncate',
                      f.path === selected
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/60'
                    )}
                    title={f.path}
                  >
                    <FileCode className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{f.path}</span>
                  </button>
                </li>
              ))}
            </ul>

            <div className="relative min-w-0">
              {active && (
                <>
                  <button
                    onClick={handleCopy}
                    className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-md text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-white/80 dark:bg-slate-900/80 hover:bg-slate-100 dark:hover:bg-slate-800"
                    title="Copy file contents"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <pre className="text-xs font-mono whitespace-pre-wrap break-words text-slate-700 dark:text-slate-300 p-4 max-h-72 overflow-y-auto">
                    {active.content}
                  </pre>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
